import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export const Login = () => {
  const navigate = useNavigate();
  const { loginWithCredentials } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      console.log('Login attempt for:', email);
      const userData = await loginWithCredentials(email, password);
      console.log('Login successful, user:', userData);
      toast.success('Connexion réussie !');
      
      // Small delay to ensure state is updated before navigation (Safari fix)
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if user needs to change temporary password
      if (userData?.password_temporary) {
        console.log('User has temporary password, redirecting to change-password...');
        navigate('/change-password', { replace: true });
        return;
      }
      
      // Normal login - redirect to dashboard
      if (userData?.system_role === 'super_admin') {
        console.log('Redirecting to superadmin...');
        navigate('/superadmin', { replace: true });
      } else {
        console.log('Redirecting to dashboard...');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2E4A3D] flex flex-col items-center justify-center px-4 py-8 overflow-hidden">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors z-20"
        data-testid="back-to-home"
      >
        <ArrowLeft size={20} />
        <span className="hidden sm:inline">Retour</span>
      </button>

      {/* Logo + Equibook text - morphed from landing page (2x smaller) */}
      <div 
        className={`flex flex-col items-center transition-all duration-700 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
        }`}
      >
        <img 
          src="/logo-equibook.svg" 
          alt="Equibook" 
          className="w-28 h-28 sm:w-36 sm:h-36 md:w-48 md:h-48 object-contain transition-all duration-700"
          style={{ filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.3))' }}
        />
        
        {/* Brand name - justified to logo width */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white mb-2 w-28 sm:w-36 md:w-48 flex justify-between mt-2">
          <span>E</span><span>q</span><span>u</span><span>i</span><span>b</span><span>o</span><span>o</span><span>k</span>
        </h1>
      </div>

      {/* Login form card - appears with animation */}
      <div 
        className={`w-full max-w-sm mt-6 sm:mt-8 transition-all duration-700 delay-200 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/20 shadow-2xl">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-serif font-semibold text-white">Connexion</h2>
            <p className="text-white/60 text-sm mt-1">Accédez à votre espace</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white/80 text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 focus:ring-white/20"
                data-testid="login-email"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-white/80 text-sm font-medium">Mot de passe</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/50 focus:ring-white/20 pr-12"
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="text-right mt-2">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-white/60 hover:text-white transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full py-2.5 bg-white text-[#2E4A3D] hover:bg-white/90 font-semibold rounded-full transition-all shadow-lg hover:shadow-xl mt-2"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <p className="text-white/60 text-sm">
              Pas encore de compte ?{' '}
              <Link 
                to="/register" 
                className="text-white font-medium hover:underline"
              >
                Créer un compte
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
